// src/components/sections/HomeChallengesPreview.jsx
import React, { useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import { motion, useInView } from 'framer-motion'
import { useAppSelector } from '../../hooks/useAppDispatch.js'
import './HomeChallengesPreview.scss'

export default function HomeChallengesPreview({ sectionData }) {
  const ref = useRef(null)
  const navigate = useNavigate()
  const isInView = useInView(ref, { once: true, margin: '-100px' })

  const { caseStudies, loading: caseStudiesLoading } = useAppSelector(
    (state) => state.caseStudies
  )

  // Animation variants (same as v1 style)
  const container = {
    hidden: { opacity: 0 },
    show: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2,
      },
    },
  }

  const item = {
    hidden: { y: 50, opacity: 0 },
    show: {
      y: 0,
      opacity: 1,
      transition: {
        type: 'spring',
        stiffness: 100,
        damping: 15,
        duration: 0.5,
      },
    },
  }

  const fadeIn = {
    hidden: { opacity: 0 },
    show: {
      opacity: 1,
      transition: {
        duration: 0.8,
        ease: 'easeOut',
      },
    },
  }

  // Skeleton when section + data not ready
  if (!sectionData && caseStudiesLoading) {
    return (
      <section className="case-studies-section">
        <div className="case-studies-container">
          <div className="animate-pulse">
            <div className="h-8 bg-black-200 rounded w-1/3 mx-auto mb-4"></div>
            <div className="h-10 bg-black-200 rounded w-48 mx-auto mb-8"></div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {[...Array(3)].map((_, i) => (
                <div key={i} className="h-[450px] bg-black-200 rounded-lg"></div>
              ))}
            </div>
          </div>
        </div>
      </section>
    )
  }

  const heading = sectionData?.heading || sectionData?.Heading
  const buttonText = sectionData?.buttonText || sectionData?.ButtonText || 'Explore All Case Studies'
  const buttonLink = sectionData?.buttonLink || sectionData?.ButtonLink || '/case-studies'

  const studies = caseStudies?.slice(0, 3) || []

  // If nothing to show, skip
  if (!heading && !studies.length && !caseStudiesLoading) {
    return null
  }

  return (
    <section className="case-studies-section" ref={ref}>
      <div className="case-studies-container">
        {/* Heading */}
        {heading && (
          <motion.h2
            className="case-studies-heading"
            initial="hidden"
            animate={isInView ? 'show' : 'hidden'}
            variants={fadeIn}
          >
            {heading.toUpperCase()}
          </motion.h2>
        )}

        {/* Cards Grid */}
        <motion.div
          className="case-studies-grid"
          variants={container}
          initial="hidden"
          animate={isInView ? 'show' : 'hidden'}
        >
          {studies.map((study, index) => {
            const title =
              study.title || study.heading_1 || 'Case Study'
            const description =
              study.summary ||
              study.subHeading ||
              study.sub_heading_1 ||
              ''
            const imageSrc =
              study.thumbnailUrl || study.imageUrl || study.hero?.imageUrl

            const capsules = [
              study.capsul_1,
              study.capsul_2,
              study.industry,
              study.category,
            ].filter(Boolean)

            const truncatedTitle =
              title && title.length > 22 ? `${title.slice(0, 22)}...` : title

            const handleCardClick = () => {
              if (study.slug) {
                navigate(`/case-studies/${study.slug}`)
              } else {
                navigate('/case-studies')
              }
            }

            return (
              <motion.div
                key={study.id || study._id || study.slug || index}
                className="case-study-card"
                onClick={handleCardClick}
                variants={item}
                whileHover={{ y: -10 }}
              >
                {/* Capsules */}
                {capsules.length > 0 && (
                  <div className="case-study-capsules">
                    {capsules.slice(0, 2).map((cap, i) => (
                      <span key={i} className="case-study-capsule">
                        {cap}
                      </span>
                    ))}
                  </div>
                )}

                {/* Image */}
                {imageSrc && (
                  <div className="case-study-image">
                    <motion.img
                      src={imageSrc}
                      alt={title}
                      initial={{ scale: 1 }}
                      whileHover={{ scale: 1.05 }}
                      transition={{ duration: 0.5 }}
                    />
                  </div>
                )}

                {/* Content */}
                <div className="case-study-content">
                  <h3
                    title={title}
                    className="case-study-title"
                  >
                    {truncatedTitle}
                  </h3>
                  {description && (
                    <p
                      title={description}
                      className="case-study-description"
                    >
                      {description}
                    </p>
                  )}
                </div>
              </motion.div>
            )
          })}
        </motion.div>

        {/* CTA Button */}
        {buttonText && (
          <motion.button
            className="cta-button"
            initial="hidden"
            animate={isInView ? 'show' : 'hidden'}
            variants={fadeIn}
            transition={{ delay: 0.4 }}
            onClick={() => {
              window.scrollTo({ top: 0, behavior: 'smooth' })
              navigate("/case-studies")
            }}
          >
            {buttonText}
          </motion.button>
        )}
      </div>
    </section>
  )
}
